import type { ChartFxIds } from "./useChartFx";

/**
 * The <defs> block for a chart. Render it as the first child of the recharts
 * chart element, passing the same `fx` the marks use.
 */
export function ChartFxDefs({
  fx,
  colors,
  direction = "vertical",
}: {
  fx: ChartFxIds;
  colors: string[];
  /** Which way the light falls: down a column, along a row, across a ring, or
   *  out from the centre — the last one domes a donut's band so it reads as a
   *  tube catching light on its outer edge rather than as a flat annulus. */
  direction?: "vertical" | "horizontal" | "diagonal" | "radial";
}) {
  const prefix = (fx as ChartFxIds & { prefix: string }).prefix;
  const coords =
    direction === "horizontal"
      ? { x1: "0", y1: "0", x2: "1", y2: "0" }
      : direction === "diagonal"
        ? { x1: "0", y1: "0", x2: "1", y2: "1" }
        : { x1: "0", y1: "0", x2: "0", y2: "1" };

  return (
    <defs>
      {colors.map((color, i) =>
        direction === "radial" ? (
          // Centred on the ring, so the band runs light at its inner edge into
          // full strength at its outer one and back off at the very rim: the
          // shading of a rounded surface, not a wash across the whole chart.
          <radialGradient
            key={i}
            id={`${prefix}-g${i}`}
            cx="50%"
            cy="50%"
            r="50%"
          >
            {/* Kept near-opaque throughout. Dropping the inner stop low enough
                to read as "lighter" let the card show through instead, which
                looked like a milky patch bleached out of the middle of every
                slice rather than like a lit surface. The roundness now comes
                from the rim and the shadow; the gradient only leans it. */}
            <stop offset="45%" stopColor={color} stopOpacity={0.9} />
            <stop offset="74%" stopColor={color} stopOpacity={0.97} />
            <stop offset="94%" stopColor={color} stopOpacity={1} />
            <stop offset="100%" stopColor={color} stopOpacity={0.94} />
          </radialGradient>
        ) : (
          <linearGradient key={i} id={`${prefix}-g${i}`} {...coords}>
            <stop offset="0%" stopColor={color} stopOpacity={0.82} />
            <stop offset="55%" stopColor={color} stopOpacity={0.96} />
            <stop offset="100%" stopColor={color} stopOpacity={1} />
          </linearGradient>
        ),
      )}

      <filter
        id={`${prefix}-shadow`}
        x="-25%"
        y="-25%"
        width="150%"
        height="160%"
      >
        <feDropShadow
          dx="0"
          dy="2"
          stdDeviation="2.5"
          floodColor="rgb(15 23 42)"
          floodOpacity="0.22"
        />
      </filter>

      <filter
        id={`${prefix}-glow`}
        x="-40%"
        y="-40%"
        width="180%"
        height="180%"
      >
        <feGaussianBlur stdDeviation="3" result="blur" />
        <feComponentTransfer in="blur" result="soft">
          <feFuncA type="linear" slope="0.55" />
        </feComponentTransfer>
        <feDropShadow
          dx="0"
          dy="2"
          stdDeviation="2"
          floodColor="rgb(15 23 42)"
          floodOpacity="0.2"
        />
        <feMerge>
          <feMergeNode in="soft" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}
